/**
 * The schedule's colours, by meaning — `docs/new-request.md` §10.3, §10.5.
 *
 * Distances read green within 3 miles, amber to 8 and grey beyond; grades
 * read Best green, Good teal, OK amber. One table each, so a strip cell, a
 * block's edge, a badge and a map circle cannot disagree about what amber is.
 *
 * The amber the boards draw for a 3–8 mile stop is the product's *scheduled*
 * amber, which lives among the status pairs rather than the plain colour
 * tokens, so it is reached through its variable — still a token, still
 * swapped in dark mode, never a hex.
 */
import type { Grade, MilesTone } from '@rowted/shared'

const MID = 'rgb(var(--status-job-scheduled-fg))'

/** The distance's own colour, for text. */
export const TONE_TEXT: Record<MilesTone, string> = {
  near: 'text-accent',
  mid: 'text-[rgb(var(--status-job-scheduled-fg))]',
  far: 'text-timeline-hour',
}

/** A dot or a block's left edge. */
export const TONE_FILL: Record<MilesTone, string> = {
  near: 'bg-accent',
  mid: 'bg-[rgb(var(--status-job-scheduled-fg))]',
  far: 'bg-timeline-hour',
}

/** A distance badge: its tint and its text. */
export const TONE_BADGE: Record<MilesTone, string> = {
  near: 'bg-accent-soft text-accent',
  mid: 'bg-amber-band-bg text-[rgb(var(--status-job-scheduled-fg))]',
  far: 'bg-line-soft text-timeline-hour',
}

/** For the map, which paints in WebGL and DOM markers: the variable itself. */
export const TONE_VAR: Record<MilesTone, string> = {
  near: 'rgb(var(--color-accent))',
  mid: MID,
  far: 'rgb(var(--color-timeline-hour))',
}

/** A grade's tag. */
export const GRADE_TAG: Record<Grade, string> = {
  best: 'bg-accent-soft text-accent-dark',
  good: 'bg-teal-band-bg text-teal-band-text',
  ok: 'bg-amber-band-bg text-amber-band-text',
}

/** A grade's *+5 min*. */
export const GRADE_TEXT: Record<Grade, string> = {
  best: 'text-accent-dark',
  good: 'text-teal-band-text',
  ok: 'text-amber-band-text',
}

/** The hatched *too short* gap and the green *fits* gap, as backgrounds. */
export const HATCH = 'repeating-linear-gradient(-45deg, rgb(var(--color-timeline-lane)) 0 6px, '
  + 'rgb(var(--color-timeline-line)) 6px 12px)'
export const FITS = 'repeating-linear-gradient(0deg, rgb(var(--color-green-band-bg)) 0 6px, '
  + 'rgb(var(--color-green-wash)) 6px 7px)'
